import { c as _c } from "react/compiler-runtime";
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useRef } from 'react';
import { CanvasCollapseProvider } from '../../hooks/use-canvas-collapse';
import InputPane from '../../section-editor/input-pane';
import OutputPane from '../../section-editor/output-pane';
import RulesPane from '../../section-editor/rules';
import TaskPane from '../../section-editor/task-pane';
import { useBuilder } from '../../store/provider';
import { HEAD_GAP, useSuggestionBuckets } from '../../store/suggestions';
import { INPUT_NODE_ID, isDecisionNode, isManagedNode, isRulesNode, isTaskNode, nodeName, OUTPUTS_NODE_ID } from '../../store/types';
import DecisionBlock from '../decision';
import ManagedBlock from '../managed';
import OutlineRail from '../outline-rail';
import GhostNode from '../suggestion/ghost-node';
const SCROLL_TOP_INSET = 32;
const scrollToNode = (root: HTMLElement, targetId: string) => {
  const el = root.querySelector<HTMLElement>(`[data-suggestion-id="${targetId}"]`) ?? root.querySelector<HTMLElement>(`[data-id="${targetId}"]`);
  if (!el) return;
  // Keep jumps inside this section's scroller instead of moving the surrounding page.
  const scroller = root.parentElement;
  if (!scroller) {
    el.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    return;
  }
  const top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - SCROLL_TOP_INSET;
  scroller.scrollTo({
    top: Math.max(top, 0),
    behavior: 'smooth'
  });
};
const Canvas = () => {
  const $ = _c(5);
  const {
    state
  } = useBuilder();
  let t0;
  if ($[0] !== state.bundle.nodes) {
    t0 = [INPUT_NODE_ID, ...state.bundle.nodes.map(_temp), OUTPUTS_NODE_ID];
    $[0] = state.bundle.nodes;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const nodeIds = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CanvasBody />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== nodeIds) {
    t2 = <div data-percy-context-container={true} className="h-full bg-primary"><CanvasCollapseProvider nodeIds={nodeIds} keepExpanded={nodeIds} defaultExpanded={nodeIds} forceExpanded={true}>{t1}</CanvasCollapseProvider></div>;
    $[3] = nodeIds;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const CanvasBody = () => {
  const $ = _c(28);
  const {
    state,
    dispatch
  } = useBuilder();
  const {
    bundle,
    scrollTarget
  } = state;
  const [nodeParam] = useQueryState("node", parseAsString);
  const rootRef = useRef(null);
  const nodes = bundle.nodes;
  const gapBuckets = useSuggestionBuckets().byGap;
  const headGapSuggestions = gapBuckets.get(HEAD_GAP) ?? [];
  let t0;
  if ($[0] !== nodeParam) {
    t0 = node => node.id === nodeParam || `${node.kind}-${node.id}` === nodeParam;
    $[0] = nodeParam;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const selected = nodes.find(t0);
  const selectedId = selected ? `${selected.kind}-${selected.id}` : nodeParam === OUTPUTS_NODE_ID ? OUTPUTS_NODE_ID : INPUT_NODE_ID;
  const scrollRef = useRef(null);
  const index = selected ? nodes.indexOf(selected) : -1;
  const suggestions = selected ? gapBuckets.get(selected.id) ?? [] : selectedId === INPUT_NODE_ID ? headGapSuggestions : [];
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      scrollRef.current?.scrollTo({
        top: 0
      });
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== selectedId) {
    t2 = [selectedId];
    $[3] = selectedId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== dispatch || $[6] !== scrollTarget) {
    t3 = () => {
      if (!scrollTarget || !rootRef.current) {
        return;
      }
      const target = rootRef.current.querySelector(`[data-id="${scrollTarget}"], [data-suggestion-id="${scrollTarget}"]`);
      if (!target) {
        return;
      }
      scrollToNode(rootRef.current, scrollTarget);
      dispatch({
        type: "scrollTo",
        id: null
      });
    };
    $[5] = dispatch;
    $[6] = scrollTarget;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== dispatch || $[9] !== nodeParam || $[10] !== nodes || $[11] !== scrollTarget) {
    t4 = [scrollTarget, dispatch, nodes, nodeParam];
    $[8] = dispatch;
    $[9] = nodeParam;
    $[10] = nodes;
    $[11] = scrollTarget;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  useEffect(t3, t4);
  const t5 = "@container relative h-full";
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <OutlineRail scrollRef={scrollRef} contentRef={rootRef} />;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = "h-full overflow-y-auto overscroll-none px-6 py-8 sm:px-10";
  const t8 = "mx-auto max-w-[720px]";
  let t9;
  if ($[14] !== bundle.inputs || $[15] !== selectedId) {
    t9 = selectedId === INPUT_NODE_ID && <InputPane inputs={bundle.inputs} />;
    $[14] = bundle.inputs;
    $[15] = selectedId;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== selectedId) {
    t10 = selectedId === OUTPUTS_NODE_ID && <OutputPane />;
    $[17] = selectedId;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = selected && <div data-percy-context-id={selected.id} data-percy-context-label={nodeName(selected)}>{isTaskNode(selected) && <TaskPane task={selected} position={index + 1} />}{isRulesNode(selected) && <RulesPane rulesNode={selected} position={index + 1} />}{isManagedNode(selected) && <ManagedBlock node={selected} position={index + 1} />}{isDecisionNode(selected) && <DecisionBlock node={selected} canMoveUp={index > 0} canMoveDown={index < nodes.length - 1} />}</div>;
  const t12 = suggestions.map(_temp2);
  let t13;
  if ($[19] !== selectedId || $[20] !== t10 || $[21] !== t11 || $[22] !== t12 || $[23] !== t9) {
    t13 = <div ref={scrollRef} className={t7}><div ref={rootRef} key={selectedId} className={t8}>{t9}{t10}{t11}{t12}</div></div>;
    $[19] = selectedId;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
    $[23] = t9;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13 || $[26] !== t6) {
    t14 = <div className={t5}>{t6}{t13}</div>;
    $[25] = t13;
    $[26] = t6;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
};
export default Canvas;
function _temp(node) {
  return node.id;
}
function _temp2(suggestion) {
  return <GhostNode key={suggestion.id} suggestion={suggestion} />;
}